import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/dashboard' },
  { key: 'vendors', label: 'Vendors', icon: 'storefront', path: '/vendors' },
  { key: 'categories', label: 'Categories', icon: 'category', path: '/categories' },
  { key: 'executives', label: 'Sales Executives', icon: 'account_circle', path: '/executives' },
  { key: 'subscriptions', label: 'Subscriptions', icon: 'receipt_long', path: '/subscriptions' },
  { key: 'approvals', label: 'Approvals', icon: 'notifications', path: '/approvals', badgeKey: 'pendingApprovals' },
  { key: 'commissions', label: 'Commission Management', icon: 'payments', path: '/commissions' },
  { key: 'certificateApplications', label: 'Vendor Certificate Applications', icon: 'verified', path: '/vendor-certificate-applications' },
  { key: 'reports', label: 'Reports', icon: 'analytics', path: '/reports' },
  { key: 'settings', label: 'Settings', icon: 'settings', path: '/settings' }
];

const GLASS_SELECTOR =
  '.glass-panel, .glass-kpi-card, .glass-category-card, .glass-directory-panel';

export default function AdminShell({
  admin,
  badges = {},
  pageTitle,
  headerExtra,
  children,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search vendors, users...'
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifOpen, setNotifOpen] = useState(false);
  const activeCardRef = useRef(null);

  // Global cursor-tracking shine for every .glass-panel / .glass-kpi-card /
  // .glass-category-card / .glass-directory-panel on the page — no per-page
  // JS needed, GlassAdmin.css reads the --mx/--my vars this sets.
  useEffect(() => {
    const handleMove = (e) => {
      const card = e.target.closest(GLASS_SELECTOR);

      if (!card) {
        if (activeCardRef.current) {
          activeCardRef.current.style.removeProperty('--mx');
          activeCardRef.current.style.removeProperty('--my');
          activeCardRef.current = null;
        }
        return;
      }

      if (activeCardRef.current && activeCardRef.current !== card) {
        activeCardRef.current.style.removeProperty('--mx');
        activeCardRef.current.style.removeProperty('--my');
      }

      const rect = card.getBoundingClientRect();
      const mx = ((e.clientX - rect.left) / rect.width) * 100;
      const my = ((e.clientY - rect.top) / rect.height) * 100;
      card.style.setProperty('--mx', `${mx}%`);
      card.style.setProperty('--my', `${my}%`);
      activeCardRef.current = card;
    };

    const handleLeaveWindow = () => {
      if (activeCardRef.current) {
        activeCardRef.current.style.removeProperty('--mx');
        activeCardRef.current.style.removeProperty('--my');
        activeCardRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseleave', handleLeaveWindow);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseleave', handleLeaveWindow);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminToken');
    navigate('/login');
  };

  const activeKey = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path))?.key || 'dashboard';

  return (
    <div className="dash-layout">
      {/* Sidebar */}
      <aside className="dash-sidebar">
        <div className="dash-sidebar-header">
          <img className="dash-sidebar-logo" src="/logo.png" alt="Tedo Bizz Logo" />
          <span className="dash-sidebar-title">Tedo Bizz</span>
        </div>
        <nav className="dash-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className={`dash-nav-link ${activeKey === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
              {item.badgeKey && badges[item.badgeKey] ? (
                <span className="dash-nav-badge">{badges[item.badgeKey]}</span>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="dash-sidebar-footer">
          <img
            className="dash-avatar"
            src={admin?.avatarUrl || 'https://api.dicebear.com/7.x/initials/svg?seed=' + (admin?.name || 'Admin')}
            alt={admin?.name || 'Admin'}
          />
          <div>
            <p className="dash-avatar-name">{admin?.name || 'Loading...'}</p>
            <p className="dash-avatar-role">{admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</p>
          </div>
        </div>
      </aside>

      <div className="dash-content">
        {/* Mobile header */}
        <header className="dash-mobile-header">
          <div className="dash-mobile-brand">
            <img className="dash-mobile-logo" src="/logo.png" alt="Tedo Bizz Logo" />
            <span className="dash-mobile-title">Tedo Bizz</span>
          </div>
          <button className="dash-icon-btn" onClick={logout} title="Log out">
            <span className="material-symbols-outlined">account_circle</span>
          </button>
        </header>

        {/* Desktop header */}
        <header className="dash-desktop-header">
          <h1 className="dash-desktop-title">{pageTitle}</h1>
          <div className="dash-header-actions">
            {headerExtra || (
              <div className="dash-search-wrapper">
                <span className="material-symbols-outlined dash-search-icon">search</span>
                <input
                  className="dash-search-input"
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue ?? ''}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                />
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <button className="dash-notif-btn" onClick={() => setNotifOpen((v) => !v)}>
                <span className="material-symbols-outlined">notifications</span>
                <span className="dash-notif-dot" />
              </button>
              {notifOpen && (
                <div className="dash-notif-dropdown">
                  <p className="dash-notif-dropdown-title">Notifications</p>
                  <p className="dash-notif-dropdown-empty">You're all caught up — no new notifications.</p>
                </div>
              )}
            </div>
            <button className="dash-icon-btn" onClick={logout} title="Log out">
              <span className="material-symbols-outlined">logout</span>
            </button>
          </div>
        </header>

        <div className="dash-body">{children}</div>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="dash-bottom-nav">
        <div className="dash-bottom-nav-inner">
          <button className={`dash-bottom-nav-link ${activeKey === 'dashboard' ? 'active' : ''}`} onClick={() => navigate('/dashboard')}>
            <span className="material-symbols-outlined">dashboard</span>
            <span>Dashboard</span>
          </button>
          <button className={`dash-bottom-nav-link ${activeKey === 'executives' ? 'active' : ''}`} onClick={() => navigate('/executives')}>
            <span className="material-symbols-outlined">group</span>
            <span>Leads</span>
          </button>
          <button className="dash-bottom-nav-link" onClick={logout}>
            <span className="material-symbols-outlined">person</span>
            <span>Profile</span>
          </button>
        </div>
      </nav>
    </div>
  );
}